import React, { useState, useEffect, useCallback } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../components/ui/Toast';
import { calculateMerchantTaxAmount, calculateMerchantTaxTotal } from '../../utils/merchantTax';
import './RecordSaleModal.css';

// Modern SVG Icons for sources
const SourceIcons = {
  PHONE_CALL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  SOCIAL_MEDIA_DM: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="4" />
      <path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94" />
    </svg>
  ),
  WALK_IN: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="13" cy="4" r="2" />
      <path d="M7 21l3-4v-6l-3-3 2-2 5 4v4l4 3" />
      <path d="M10 11l-2 7" />
    </svg>
  ),
  MARKET_STALL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  EMAIL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  REFERRAL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  WHOLESALE: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  OTHER: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  )
};

// Modern SVG Icons for payment methods
const PaymentIcons = {
  CASH: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <circle cx="12" cy="12" r="3" />
      <line x1="1" y1="10" x2="3" y2="10" />
      <line x1="21" y1="10" x2="23" y2="10" />
    </svg>
  ),
  BANK_TRANSFER: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
    </svg>
  ),
  POS_TERMINAL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  ),
  MOBILE_MONEY: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  ),
  CHEQUE: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  CREDIT: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  CRYPTOCURRENCY: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.5 9a3 3 0 015 0v6a3 3 0 01-5 0" />
      <line x1="12" y1="3" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="21" />
    </svg>
  ),
  OTHER: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  )
};

const SALE_SOURCES = [
  { value: 'PHONE_CALL', label: 'Phone Call' },
  { value: 'SOCIAL_MEDIA_DM', label: 'Social Media DM' },
  { value: 'WALK_IN', label: 'Walk-in' },
  { value: 'MARKET_STALL', label: 'Market/Pop-up' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'WHOLESALE', label: 'Wholesale' },
  { value: 'OTHER', label: 'Other' }
];

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'POS_TERMINAL', label: 'POS Terminal' },
  { value: 'MOBILE_MONEY', label: 'Mobile Money' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CREDIT', label: 'Credit (Owes)' },
  { value: 'CRYPTOCURRENCY', label: 'Crypto' },
  { value: 'OTHER', label: 'Other' }
];

export default function RecordSaleModal({ isOpen, onClose, onSuccess }) {
  const { getAxios } = useSecureAuth();
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const toast = useToast();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [taxSettings, setTaxSettings] = useState({ enabled: false, rate: 0, name: 'Tax', inclusive: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    manualSaleSource: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    items: [],
    externalPaymentMethod: '',
    externalPaymentRef: '',
    // Credit-sale fields (only relevant when externalPaymentMethod === 'CREDIT').
    // hasUpfront toggles the partial-payment input; amountPaidUpfront is the
    // amount the customer paid at the time of sale; receivableDueDate sets
    // when the rest is due (defaults to +30d at the backend if blank).
    hasUpfront: false,
    amountPaidUpfront: '',
    receivableDueDate: '',
    originalSaleDate: new Date().toISOString().split('T')[0],
    requiresFulfillment: false,
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      country: 'Nigeria'
    },
    manualSaleNotes: ''
  });

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const axios = getAxios();
      const response = await axios.get('/merchants/products', { params: { limit: 200 } });
      const productList = response.data.products || response.data || [];
      setProducts(Array.isArray(productList) ? productList : []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    }
  }, [getAxios]);

  const fetchTaxSettings = useCallback(async () => {
    try {
      const axios = getAxios();
      const response = await axios.get('/merchant/payment-gateways/tax-settings');
      const next = response.data?.data || response.data?.taxSettings || response.data;
      if (next) {
        setTaxSettings({
          enabled: next.enabled === true,
          rate: Number(next.rate || 0),
          name: next.name || 'Tax',
          inclusive: next.inclusive === true
        });
      }
    } catch (error) {
      console.error('Failed to fetch merchant tax settings:', error);
    }
  }, [getAxios]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFormData({
        manualSaleSource: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        items: [],
        externalPaymentMethod: '',
        externalPaymentRef: '',
        hasUpfront: false,
        amountPaidUpfront: '',
        receivableDueDate: '',
        originalSaleDate: new Date().toISOString().split('T')[0],
        requiresFulfillment: false,
        shippingAddress: { street: '', city: '', state: '', country: 'Nigeria' },
        manualSaleNotes: ''
      });
      setSearchQuery('');
      setSearchResults([]);
      fetchProducts();
      fetchTaxSettings();
    }
  }, [isOpen, fetchProducts, fetchTaxSettings]);

  // Search products
  useEffect(() => {
    if (searchQuery.trim() && products.length > 0) {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query)
      );
      setSearchResults(filtered.slice(0, 10));
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, products]);

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const taxRate = taxSettings.enabled ? Number(taxSettings.rate || 0) : 0;
  const taxTotal = calculateMerchantTaxAmount(subtotal, taxSettings);
  const total = calculateMerchantTaxTotal(subtotal, taxSettings);

  // Add item
  const addItem = (product) => {
    const existingIndex = formData.items.findIndex(i => i.productId === product.id);
    if (existingIndex >= 0) {
      const newItems = [...formData.items];
      newItems[existingIndex].qty += 1;
      setFormData({ ...formData, items: newItems });
    } else {
      setFormData({
        ...formData,
        items: [...formData.items, {
          productId: product.id,
          name: product.name,
          price: parseFloat(product.price) || 0,
          qty: 1
        }]
      });
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  // Add custom item
  const addCustomItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        productId: null,
        name: '',
        price: 0,
        qty: 1,
        isCustom: true
      }]
    });
  };

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'price' || field === 'qty' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  // Remove item
  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  // Submit sale
  const handleSubmit = async () => {
    if (!formData.manualSaleSource) {
      toast.error('Please select sale source');
      return;
    }
    if (!formData.externalPaymentMethod) {
      toast.error('Please select payment method');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const saleDate = new Date(formData.originalSaleDate);
    if (saleDate > new Date()) {
      toast.error('Sale date cannot be in the future');
      return;
    }

    // Credit-sale validation. If the merchant ticked "Customer paid
    // something upfront" but the amount is missing / zero / >= total,
    // refuse to submit — the partial flow is ambiguous otherwise.
    if (formData.externalPaymentMethod === 'CREDIT' && formData.hasUpfront) {
      const upfrontNum = parseFloat(formData.amountPaidUpfront);
      if (!upfrontNum || upfrontNum <= 0) {
        toast.error('Enter the amount paid upfront, or untick the option.');
        return;
      }
      if (total > 0 && upfrontNum >= total) {
        toast.error('Upfront amount is >= total — pick a non-CREDIT payment method instead.');
        return;
      }
    }

    setLoading(true);
    try {
      const axios = getAxios();
      // Strip UI-only fields from the payload; backend expects the
      // resolved amount (or absence of it) to decide PAID/PARTIAL/UNPAID.
      const { hasUpfront, ...payload } = formData;
      const response = await axios.post('/merchant/manual-orders/record-sale', {
        ...payload,
        // Only forward the upfront amount if the merchant actually
        // captured one — otherwise let backend default to fully unpaid.
        amountPaidUpfront:
          formData.externalPaymentMethod === 'CREDIT' && formData.hasUpfront
            ? parseFloat(formData.amountPaidUpfront) || 0
            : undefined,
        subtotal,
        taxTotal,
        total,
        shippingFee: 0,
        discount: 0
      });

      if (response.data.success) {
        toast.success('Sale recorded successfully!');
        onSuccess && onSuccess(response.data.order);
        onClose();
      } else {
        toast.error(response.data.error || 'Failed to record sale');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record sale');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rs-modal-overlay" onClick={onClose}>
      <div className="rs-modal" onClick={e => e.stopPropagation()}>
        <div className="rs-modal-header">
          <h2>Record Sale</h2>
          <p className="rs-modal-subtitle">Record a completed sale (money already received)</p>
          <button className="rs-close-btn" onClick={onClose}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="rs-steps">
          {['Source', 'Customer', 'Items', 'Payment', 'Details', 'Review'].map((label, i) => (
            <div key={i} className={`rs-step ${step >= i + 1 ? 'active' : ''}`}>
              <span className="rs-step-num">{i + 1}</span>
              <span className="rs-step-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="rs-modal-body">
          {/* Step 1: Sale Source */}
          {step === 1 && (
            <div className="rs-step-content">
              <h3>Where did this sale come from?</h3>
              <div className="rs-source-grid">
                {SALE_SOURCES.map(source => (
                  <button
                    key={source.value}
                    className={`rs-source-btn ${formData.manualSaleSource === source.value ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, manualSaleSource: source.value })}
                  >
                    <span className="rs-source-icon">{SourceIcons[source.value]}</span>
                    <span className="rs-source-label">{source.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Customer Info */}
          {step === 2 && (
            <div className="rs-step-content">
              <h3>Customer Information (Optional)</h3>
              <p className="rs-hint">Leave blank for anonymous/walk-in sales</p>
              <div className="rs-form-group">
                <label>Customer Name</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={e => setFormData({ ...formData, customerName: e.target.value })}
                  placeholder="Enter customer name (optional)"
                />
              </div>
              <div className="rs-form-row">
                <div className="rs-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={e => setFormData({ ...formData, customerEmail: e.target.value })}
                    placeholder="customer@email.com"
                  />
                </div>
                <div className="rs-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.customerPhone}
                    onChange={e => setFormData({ ...formData, customerPhone: e.target.value })}
                    placeholder="+234..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Items */}
          {step === 3 && (
            <div className="rs-step-content">
              <h3>What was sold?</h3>
              <div className="rs-product-search">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery && setShowResults(true)}
                  placeholder="Search products by name or SKU..."
                />
                {showResults && searchResults.length > 0 && (
                  <div className="rs-search-results">
                    {searchResults.map(product => (
                      <button
                        key={product.id}
                        className="rs-search-item"
                        onClick={() => addItem(product)}
                      >
                        <span>{product.name}</span>
                        <span>{formatInDisplayCurrency(product.price, accountCurrency)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {showResults && searchQuery && searchResults.length === 0 && (
                  <div className="rs-search-results">
                    <div className="rs-search-empty">No products found matching "{searchQuery}"</div>
                  </div>
                )}
              </div>
              <button className="rs-add-custom-btn" onClick={addCustomItem}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Custom Item
              </button>

              <div className="rs-items-list">
                {formData.items.length === 0 ? (
                  <p className="rs-no-items">No items added yet. Search for products above or add a custom item.</p>
                ) : (
                  formData.items.map((item, index) => (
                    <div key={index} className="rs-item-row">
                      {item.isCustom ? (
                        <input
                          type="text"
                          className="rs-item-name-input"
                          value={item.name}
                          onChange={e => updateItem(index, 'name', e.target.value)}
                          placeholder="Item name"
                        />
                      ) : (
                        <span className="rs-item-name">{item.name}</span>
                      )}
                      <input
                        type="number"
                        className="rs-item-qty"
                        value={item.qty}
                        onChange={e => updateItem(index, 'qty', e.target.value)}
                        min="1"
                      />
                      <input
                        type="number"
                        className="rs-item-price"
                        value={item.price}
                        onChange={e => updateItem(index, 'price', e.target.value)}
                        step="0.01"
                        placeholder="Price"
                      />
                      <span className="rs-item-total">
                        {formatInDisplayCurrency(item.price * item.qty, accountCurrency)}
                      </span>
                      <button className="rs-remove-btn" onClick={() => removeItem(index)}>
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="rs-totals">
                <div className="rs-total-row">
                  <span>Subtotal</span>
                  <span>{formatInDisplayCurrency(subtotal, accountCurrency)}</span>
                </div>
                {taxTotal > 0 && (
                  <div className="rs-total-row">
                    <span>{taxSettings.name || 'Tax'} ({taxRate}%{taxSettings.inclusive ? ' incl.' : ''})</span>
                    <span>{formatInDisplayCurrency(taxTotal, accountCurrency)}</span>
                  </div>
                )}
                <div className="rs-total-row rs-grand-total">
                  <span>Total</span>
                  <span>{formatInDisplayCurrency(total, accountCurrency)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Payment */}
          {step === 4 && (
            <div className="rs-step-content">
              <h3>How was payment received?</h3>
              <div className="rs-payment-grid">
                {PAYMENT_METHODS.map(method => (
                  <button
                    key={method.value}
                    className={`rs-payment-btn ${formData.externalPaymentMethod === method.value ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, externalPaymentMethod: method.value })}
                  >
                    <span className="rs-payment-icon">{PaymentIcons[method.value]}</span>
                    <span className="rs-payment-label">{method.label}</span>
                  </button>
                ))}
              </div>

              {formData.externalPaymentMethod === 'CREDIT' && (
                <>
                  <div className="rs-credit-warning">
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>This will be tracked as a receivable in Finance (money the customer owes).</span>
                  </div>

                  <div className="rs-form-group" style={{ marginTop: 12 }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        fontSize: 13,
                        lineHeight: 1.4,
                        userSelect: 'none',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.hasUpfront}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hasUpfront: e.target.checked,
                            amountPaidUpfront: e.target.checked ? formData.amountPaidUpfront : ''
                          })
                        }
                        style={{ flexShrink: 0, width: 16, height: 16, margin: 0, cursor: 'pointer' }}
                      />
                      <span>Customer paid something upfront</span>
                    </label>
                  </div>

                  {formData.hasUpfront && (
                    <div className="rs-form-row">
                      <div className="rs-form-group">
                        <label>Amount paid upfront (₦)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={total > 0 ? total - 0.01 : undefined}
                          value={formData.amountPaidUpfront}
                          onChange={(e) =>
                            setFormData({ ...formData, amountPaidUpfront: e.target.value })
                          }
                          placeholder="0.00"
                        />
                        <small style={{ color: '#6b7280', fontSize: 11 }}>
                          {total > 0
                            ? `Order total ₦${total.toLocaleString()} — remaining becomes the receivable.`
                            : 'Add line items first to see the total.'}
                        </small>
                      </div>
                      <div className="rs-form-group">
                        <label>Due date</label>
                        <input
                          type="date"
                          value={formData.receivableDueDate}
                          onChange={(e) =>
                            setFormData({ ...formData, receivableDueDate: e.target.value })
                          }
                        />
                        <small style={{ color: '#6b7280', fontSize: 11 }}>
                          Optional — defaults to +30 days from sale.
                        </small>
                      </div>
                    </div>
                  )}

                  {!formData.hasUpfront && (
                    <div className="rs-form-group" style={{ marginTop: 8 }}>
                      <label>Due date</label>
                      <input
                        type="date"
                        value={formData.receivableDueDate}
                        onChange={(e) =>
                          setFormData({ ...formData, receivableDueDate: e.target.value })
                        }
                        style={{ maxWidth: 200 }}
                      />
                      <small style={{ color: '#6b7280', fontSize: 11 }}>
                        Optional — defaults to +30 days from sale.
                      </small>
                    </div>
                  )}
                </>
              )}

              <div className="rs-form-group">
                <label>Payment Reference (Optional)</label>
                <input
                  type="text"
                  value={formData.externalPaymentRef}
                  onChange={e => setFormData({ ...formData, externalPaymentRef: e.target.value })}
                  placeholder="Transaction reference, receipt number, etc."
                />
              </div>
            </div>
          )}

          {/* Step 5: Details */}
          {step === 5 && (
            <div className="rs-step-content">
              <h3>Additional Details</h3>

              <div className="rs-form-group">
                <label>Sale Date</label>
                <input
                  type="date"
                  value={formData.originalSaleDate}
                  onChange={e => setFormData({ ...formData, originalSaleDate: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
                <small className="rs-hint">When did this sale actually happen?</small>
              </div>

              <div className="rs-toggle-group">
                <label className="rs-toggle">
                  <input
                    type="checkbox"
                    checked={formData.requiresFulfillment}
                    onChange={e => setFormData({ ...formData, requiresFulfillment: e.target.checked })}
                  />
                  <span className="rs-toggle-slider"></span>
                  <span className="rs-toggle-label">Requires Delivery/Fulfillment</span>
                </label>
                <small className="rs-hint">
                  {formData.requiresFulfillment
                    ? 'This sale needs to be delivered to the customer'
                    : 'Customer already received the items (e.g., walk-in purchase)'}
                </small>
              </div>

              {formData.requiresFulfillment && (
                <>
                  <h4>Shipping Address</h4>
                  <div className="rs-form-group">
                    <input
                      type="text"
                      value={formData.shippingAddress.street}
                      onChange={e => setFormData({
                        ...formData,
                        shippingAddress: { ...formData.shippingAddress, street: e.target.value }
                      })}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="rs-form-row">
                    <div className="rs-form-group">
                      <input
                        type="text"
                        value={formData.shippingAddress.city}
                        onChange={e => setFormData({
                          ...formData,
                          shippingAddress: { ...formData.shippingAddress, city: e.target.value }
                        })}
                        placeholder="City"
                      />
                    </div>
                    <div className="rs-form-group">
                      <input
                        type="text"
                        value={formData.shippingAddress.state}
                        onChange={e => setFormData({
                          ...formData,
                          shippingAddress: { ...formData.shippingAddress, state: e.target.value }
                        })}
                        placeholder="State"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="rs-form-group">
                <label>Notes</label>
                <textarea
                  value={formData.manualSaleNotes}
                  onChange={e => setFormData({ ...formData, manualSaleNotes: e.target.value })}
                  placeholder="Additional notes about this sale..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 6: Review */}
          {step === 6 && (
            <div className="rs-step-content">
              <h3>Review & Confirm</h3>

              <div className="rs-review-card">
                <div className="rs-review-row">
                  <span>Source</span>
                  <span>{SALE_SOURCES.find(s => s.value === formData.manualSaleSource)?.label}</span>
                </div>
                <div className="rs-review-row">
                  <span>Customer</span>
                  <span>{formData.customerName || 'Anonymous'}</span>
                </div>
                <div className="rs-review-row">
                  <span>Items</span>
                  <span>{formData.items.length} item(s)</span>
                </div>
                <div className="rs-review-row">
                  <span>Payment</span>
                  <span>{PAYMENT_METHODS.find(m => m.value === formData.externalPaymentMethod)?.label}</span>
                </div>
                <div className="rs-review-row">
                  <span>Sale Date</span>
                  <span>{new Date(formData.originalSaleDate).toLocaleDateString()}</span>
                </div>
                <div className="rs-review-row">
                  <span>Fulfillment</span>
                  <span>{formData.requiresFulfillment ? 'Needs Delivery' : 'Already Fulfilled'}</span>
                </div>
                <div className="rs-review-row rs-review-total">
                  <span>Total</span>
                  <span>{formatInDisplayCurrency(total, accountCurrency)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="rs-modal-footer">
          {step > 1 && (
            <button className="rs-btn rs-btn-secondary" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          <div className="rs-footer-spacer" />
          {step < 6 ? (
            <button
              className="rs-btn rs-btn-primary"
              onClick={() => {
                if (step === 1 && !formData.manualSaleSource) {
                  toast.warning('Please select a sale source');
                  return;
                }
                if (step === 3 && formData.items.length === 0) {
                  toast.warning('Please add at least one item');
                  return;
                }
                if (step === 4 && !formData.externalPaymentMethod) {
                  toast.warning('Please select payment method');
                  return;
                }
                setStep(step + 1);
              }}
            >
              Continue
            </button>
          ) : (
            <button
              className="rs-btn rs-btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Recording...' : 'Record Sale'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
